import { z } from "zod";

/**
 * What the student chose to do with a lesson at its start. Verb plus scope, all
 * three: the two vocabularies below use "resume" for the LESSON, this one for a
 * bite.
 *
 * Wire values, shared with the backend's StartChoice
 * (`backend/constants/start_choice.py`) — keep the two in sync. The names
 * differ because this side also names the vocabulary of apply_resume_action.
 */
export const RESUME_ACTION = {
  RESUME_BITE: "resume_bite",
  RESTART_BITE: "restart_bite",
  RESTART_LESSON: "restart_lesson",
} as const;
export type ResumeAction = (typeof RESUME_ACTION)[keyof typeof RESUME_ACTION];

/** Where the student arrived from. Reported to analytics, decides nothing. */
export const ENTRY_SOURCE = {
  REFRESH: "refresh",
  DASHBOARD: "dashboard",
  RECONNECT: "reconnect",
  LTI: "lti",
} as const;
export type EntrySource = (typeof ENTRY_SOURCE)[keyof typeof ENTRY_SOURCE];

/**
 * What the dashboard card guessed from the lesson's status, before discovery
 * answered; it picks which modal opens. "resume" here means the LESSON.
 */
export const ENTRY_INTENT = {
  DEFAULT: "default",
  LEARN_AGAIN: "learn_again",
  RESUME: "resume",
} as const;
export type EntryIntent = (typeof ENTRY_INTENT)[keyof typeof ENTRY_INTENT];

export const CACHED_BITE_COMPLETION_STATE = {
  FRESH: "fresh",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
} as const;
export type CachedBiteCompletionState =
  (typeof CACHED_BITE_COMPLETION_STATE)[keyof typeof CACHED_BITE_COMPLETION_STATE];

export const CACHED_BITE_CACHE_REASON = {
  CHECKPOINT: "checkpoint",
  HISTORY: "history",
  VISUAL_STATE: "visual_state",
} as const;
export type CachedBiteCacheReason =
  | (typeof CACHED_BITE_CACHE_REASON)[keyof typeof CACHED_BITE_CACHE_REASON]
  | null;

export interface LessonEntryContext {
  initialSlideIndex: string | null;
  entrySource: EntrySource;
  entryIntent: EntryIntent;
}

export const ResumePreviewSchema = z.object({
  slide_index: z.coerce.number().int().nonnegative(),
  fragment_index: z.coerce.number().int().min(-1),
  saved_at: z.coerce.number().int().nonnegative(),
});

export const RehydratePayloadSchema = z.object({
  slide_index: z.coerce.number().int().nonnegative(),
  fragment_index: z.coerce.number().int().min(-1),
  revealed_fragment_ids: z
    .array(z.coerce.number().int().nonnegative())
    .default([]),
  student_inputs: z.record(z.string(), z.unknown()).default({}),
  saved_at: z.coerce.number().int().nonnegative(),
  history: z.array(z.record(z.string(), z.unknown())).optional(),
  notepad_used: z.boolean().optional(),
});

export const CachedBiteMetadataSchema = z.object({
  has_cached_state: z.boolean().default(false),
  completion_state: z
    .enum(["fresh", "in_progress", "completed"])
    .default("fresh"),
  cache_reason: z
    .enum(["checkpoint", "history", "visual_state"])
    .nullable()
    .optional(),
  state_version: z.coerce.number().int().nonnegative().default(0),
  rehydrate_payload: RehydratePayloadSchema.nullable().optional(),
  last_tutor_message: z.string().nullable().optional(),
});

export type ResumePreview = z.infer<typeof ResumePreviewSchema>;
export type RehydratePayload = Omit<
  z.infer<typeof RehydratePayloadSchema>,
  "student_inputs"
> & {
  student_inputs: Record<string, string>;
};
export type CachedBiteMetadata = Omit<
  z.infer<typeof CachedBiteMetadataSchema>,
  "rehydrate_payload"
> & {
  rehydrate_payload?: RehydratePayload | null;
};

export const getLessonEntryContext = (
  params: URLSearchParams,
): LessonEntryContext => ({
  initialSlideIndex: params.get("slideIndex"),
  entrySource: params.get("ltiLaunchRequestId")
    ? "lti"
    : params.get("sessionId")
      ? "refresh"
      : "dashboard",
  entryIntent: (() => {
    const entryIntent = params.get("entryIntent");
    if (entryIntent === "learn_again") return "learn_again";
    if (entryIntent === "resume") return "resume";
    return "default";
  })(),
});

export const normalizeStudentInputs = (
  inputs: Record<string, unknown> | undefined,
): Record<string, string> => {
  const normalized: Record<string, string> = {};
  if (!inputs || typeof inputs !== "object") {
    return normalized;
  }
  Object.entries(inputs).forEach(([actionId, value]) => {
    normalized[actionId] = value == null ? "" : String(value);
  });
  return normalized;
};

/**
 * True when resuming would pair a system prompt in one language with a message
 * history recorded in another. An unrecorded session language stays resumable.
 */
export const isResumeLockedByLanguage = (
  sessionLanguage: string | null | undefined,
  selectedLanguage: string | null | undefined,
): boolean =>
  Boolean(sessionLanguage && selectedLanguage) &&
  sessionLanguage !== selectedLanguage;

/**
 * What a failed apply_resume_action leaves the student to do. No answer erases
 * anything: losing progress is only ever the student's own click.
 */
export const RESUME_FAILURE = {
  /** The choice was made against state that has moved on; offer it again. */
  STATE_CHANGED: "state_changed",
  /** Nothing is left to resume; offer the choice that remains. */
  NOTHING_TO_RESUME: "nothing_to_resume",
  /** Nothing changed on the server; the choice on screen still stands. */
  TRANSIENT: "transient",
} as const;
export type ResumeFailure =
  (typeof RESUME_FAILURE)[keyof typeof RESUME_FAILURE];

/**
 * 400 stays transient. Every 400 the endpoint sends is about the request, not
 * the lesson, so a fresh read would only reopen the same choice under a
 * message ("your lesson changed") that is not true.
 */
export const resolveResumeFailure = (
  status: number | undefined,
): ResumeFailure => {
  if (status === 409) return RESUME_FAILURE.STATE_CHANGED;
  if (status === 404) return RESUME_FAILURE.NOTHING_TO_RESUME;
  return RESUME_FAILURE.TRANSIENT;
};

export interface SessionLanguageSeed {
  /** Remember this lesson as seeded, so reopening does not fight the student. */
  shouldMarkSeeded: boolean;
  /** Language to switch to, or null to leave the selection alone. */
  language: string | null;
}

/**
 * What a start screen should do about the language its session was begun in.
 *
 * Without this a student whose stored preference differs from the session's
 * language finds Resume locked before touching anything.
 *
 * The two answers are separate because the order is not obvious: a lesson
 * counts as seeded even when the language already matched, so that a later
 * reopen cannot undo a change the student made in between.
 */
export const resolveSessionLanguageSeed = ({
  sessionLanguage,
  selectedLanguage,
  lessonId,
  seededForLessonId,
}: {
  sessionLanguage: string | null | undefined;
  selectedLanguage: string | null | undefined;
  lessonId: number | string | null | undefined;
  seededForLessonId: number | string | null;
}): SessionLanguageSeed => {
  if (!sessionLanguage || lessonId == null) {
    return { shouldMarkSeeded: false, language: null };
  }
  if (seededForLessonId === lessonId) {
    return { shouldMarkSeeded: false, language: null };
  }
  if (sessionLanguage === selectedLanguage) {
    return { shouldMarkSeeded: true, language: null };
  }
  return { shouldMarkSeeded: true, language: sessionLanguage };
};

export const parseResumePreviewPayload = (
  value: unknown,
): ResumePreview | null => {
  const parsed = ResumePreviewSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseRehydratePayload = (
  value: unknown,
): RehydratePayload | null => {
  const parsed = RehydratePayloadSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    ...parsed.data,
    student_inputs: normalizeStudentInputs(parsed.data.student_inputs),
  };
};
